import { useRef } from 'react'

import { AdPlatform, systemTimingEvent } from '@marketplace-web/ads/ads-data'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { clientSideMetrics } from '@marketplace-web/observability/client-metrics-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'

import { MS_PER_SECOND } from '../../constants/date'

type Props = {
  countryCode?: string
  placementId: string
  platform: AdPlatform
}

function useAdLoadtimeLogging({ countryCode, placementId, platform }: Props) {
  const isAdsLoadTimeClientSideMetricsEnabled = useFeatureSwitch(
    'web_ads_load_time_client_side_metrics',
  )

  const { screen } = useSession()
  const { track } = useTracking()

  const adRequestTimestampRef = useRef(0)

  const onRequest = () => {
    if (!isAdsLoadTimeClientSideMetricsEnabled) return

    adRequestTimestampRef.current = Date.now()
  }

  const onLoad = () => {
    if (!adRequestTimestampRef.current || !isAdsLoadTimeClientSideMetricsEnabled || !countryCode)
      return

    const adLoadTime = Date.now() - adRequestTimestampRef.current

    const data = JSON.stringify({
      placement_id: placementId,
      country_code: countryCode,
      is_mobile_web: platform === AdPlatform.Mobile,
    })

    track(
      systemTimingEvent({
        screen,
        section: 'ad_load',
        duration: adLoadTime,
        completionState: 'succeeded',
        data,
      }),
    )

    clientSideMetrics
      .histogram('ad_load', {
        country_code: countryCode,
        placement_id: placementId,
        platform,
      })
      .observe(adLoadTime / MS_PER_SECOND)
  }

  return { onRequest, onLoad }
}

export default useAdLoadtimeLogging
